import { Reveal } from "@components/ui";

interface ApplianceCard {
  title: string;
  icon: React.ReactNode;
  color: string;
  bullets: string[];
}

const applianceCards: ApplianceCard[] = [
  {
    title: "Stoves & Cookers",
    color: "#f24d2b",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6 md:w-7 md:h-7">
        <path d="M12 22c3.5 0 6-2.5 6-6 0-4-3-7-6-10-3 3-6 6-6 10 0 3.5 2.5 6 6 6z" stroke="currentColor" strokeWidth="1.5" />
        <path d="M10 16a2 2 0 104 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
    bullets: [
      "Ensure proper ventilation in kitchens",
      "Install extractor fan or hood",
      "Use ESMA-certified appliances only",
    ],
  },
  {
    title: "Water Heaters (Geysers)",
    color: "#2563eb",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6 md:w-7 md:h-7">
        <path d="M12 3v1m0 16v1m-7-9H4m16 0h1M6.3 6.3l.7.7m10-0.7l-.7.7M6.3 17.7l.7-.7m10 .7l-.7-.7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.5" />
      </svg>
    ),
    bullets: [
      "Install only in well-ventilated areas",
      "Never install gas heaters in bedrooms or bathrooms",
      "Regularly check exhaust flues for blockage",
    ],
  },
  {
    title: "Barbecue Grills",
    color: "#269d0e",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6 md:w-7 md:h-7">
        <path d="M5 12h14M4 8h16l-1.5 8H5.5L4 8z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M8 16v4M16 16v4M10 4v4M14 4v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
    bullets: [
      "Use only in fully ventilated outdoor areas",
      "Keep at least 2 meters away from buildings",
    ],
  },
];

export function SafeAppliancesSection() {
  return (
    <section className="w-full bg-white overflow-hidden px-5 md:px-10 lg:px-[180px] py-12 md:py-16 lg:py-[100px]">
      <div className="w-full max-w-[1368px] mx-auto flex flex-col items-center gap-10 md:gap-14 lg:gap-16">
        {/* Title */}
        <Reveal direction="up">
          <div className="flex flex-col items-center gap-4 max-w-[1112px] mx-auto text-center">
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#122d63]/8 text-[#122d63] text-[13px] md:text-[15px] font-semibold tracking-[-0.02em] uppercase">
              Appliance Safety
            </span>
            <h2 className="text-[28px] md:text-[44px] lg:text-[54px] font-semibold tracking-[-0.04em] text-[#122d63] leading-[1.1]">
              Safe Use of Gas Appliances
            </h2>
            <p className="text-[14px] md:text-base lg:text-[22px] tracking-[-0.04em] text-[#62636c] max-w-[800px]">
              Follow these appliance-specific guidelines to ensure safe
              operation in your home.
            </p>
          </div>
        </Reveal>

        {/* Cards */}
        <Reveal direction="up" className="w-full">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {applianceCards.map((card, index) => (
              <div
                key={index}
                className="group rounded-[20px] bg-[#f8f9fb] hover:bg-white p-6 md:p-7 lg:p-8 flex flex-col gap-6 hover:shadow-[0_8px_32px_rgba(0,0,0,0.08)] transition-all duration-300 border border-transparent hover:border-[#e8e9ed]"
              >
                {/* Icon + Title */}
                <div className="flex items-center gap-3.5">
                  <div
                    className="w-12 h-12 md:w-13 md:h-13 rounded-[14px] flex items-center justify-center shrink-0 transition-colors duration-300"
                    style={{
                      backgroundColor: `${card.color}15`,
                      color: card.color,
                    }}
                  >
                    {card.icon}
                  </div>
                  <h3 className="text-[18px] md:text-[21px] lg:text-[24px] font-bold tracking-[-0.04em] text-[#1e1f24]">
                    {card.title}
                  </h3>
                </div>

                {/* Bullets */}
                <div className="flex flex-col gap-3">
                  {card.bullets.map((bullet, bIndex) => (
                    <div key={bIndex} className="flex items-start gap-3">
                      <div
                        className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5"
                        style={{ backgroundColor: `${card.color}15` }}
                      >
                        <svg viewBox="0 0 12 12" className="w-3 h-3">
                          <path
                            d="M10 3L4.5 8.5 2 6"
                            stroke={card.color}
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <p className="text-[13px] md:text-[15px] lg:text-[17px] tracking-[-0.03em] text-[#4a4b52] leading-[1.5]">
                        {bullet}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
